import { TaskTimeLogModel } from "@jugl-web/rest-api/tasks/models/TaskTimeLog/TaskTimeLogModel";
import { Alert, Button, MobileDrawer } from "@jugl-web/ui-kit";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { useFormattedDate } from "@jugl-web/utils/hooks/useFormattedDate";
import { FC, useState } from "react";
import { useTimeLogActions } from "../../hooks/useTimeLogActions";

interface DeleteTimeLogConfirmationDialogProps {
  isOpen: boolean;
  entityId: string;
  taskId: string;
  timeLog: TaskTimeLogModel | null;
  onClose: () => void;
  onAfterDelete?: (timeLogId: string) => void;
}

export const DeleteTimeLogConfirmationDialog: FC<
  DeleteTimeLogConfirmationDialogProps
> = ({ isOpen, entityId, taskId, timeLog, onClose, onAfterDelete }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isMobile } = useAppVariant();

  const { formatDateLabel } = useFormattedDate();
  const { t } = useTranslations();

  const { deleteTimeLog } = useTimeLogActions();

  const handleDelete = async () => {
    if (!timeLog) {
      return;
    }

    setIsSubmitting(true);

    try {
      await deleteTimeLog({
        entityId,
        taskId,
        timeLogId: timeLog.id,
        meta: {
          successMessage: t({
            id: "feedback.time-log-deleted",
            defaultMessage: "Time log has been deleted",
          }),
          getErrorMessage: () =>
            t({
              id: "feedback.failed-to-delete-time-log",
              defaultMessage: "Failed to delete time log",
            }),
        },
      });

      onAfterDelete?.(timeLog.id);
      onClose();
    } catch {
      // Error UI handled via meta + apiResultMiddleware
    } finally {
      setIsSubmitting(false);
    }
  };

  const labels = {
    title: t({
      id: "tasks-page.delete-time-log",
      defaultMessage: "Delete Log",
    }),
    message: t(
      {
        id: "tasks-page.delete-time-log-confirmation-description",
        defaultMessage: "Are you sure you want to delete log from {date}?",
      },
      {
        date: (
          <span className="font-medium text-primary-800">
            {formatDateLabel(timeLog?.startDate || new Date())}
          </span>
        ),
      }
    ),
    delete: t({
      id: "common.delete",
      defaultMessage: "Delete",
    }),
    cancel: t({
      id: "common.cancel",
      defaultMessage: "Cancel",
    }),
  };

  if (isMobile) {
    return (
      <MobileDrawer
        isOpen={isOpen}
        header={{ title: labels.title }}
        onClose={onClose}
      >
        <MobileDrawer.Content>
          <p className="mt-2 mb-10 font-secondary text-sm leading-[21px] text-onyx">
            {labels.message}
          </p>
          <div className="flex items-center gap-2.5">
            <Button
              variant="tertiary"
              className="h-10 w-full shrink font-medium"
              onClick={onClose}
            >
              {labels.cancel}
            </Button>
            <Button
              variant="danger"
              className="h-10 w-full shrink font-medium"
              onClick={handleDelete}
              isDisabled={isSubmitting}
            >
              {labels.delete}
            </Button>
          </div>
        </MobileDrawer.Content>
      </MobileDrawer>
    );
  }

  return (
    <Alert
      isOpen={isOpen}
      title={labels.title}
      content={labels.message}
      buttons={[
        {
          text: labels.cancel,
          role: "close",
        },
        {
          text: labels.delete,
          color: "tertiary",
          isDisabled: isSubmitting,
          onClick: handleDelete,
        },
      ]}
      onRequestClose={onClose}
    />
  );
};
